import { useEffect, useContext, useState } from 'react'
import { withRouter } from 'react-router-dom'
import { ContentHeader } from 'components/shared/content'
import * as H from 'history'
import { MeasureLedger, MeasureLedgerSearchForm } from 'entities/measure_ledger'

import moment from 'moment'
import { A4 } from 'components/shared/print'
import { Flex } from 'components/shared/flex'
import { setPageSize } from 'utils/util'
import { Button } from 'react-bootstrap'
import { GlobalStateContext } from 'contexts/global_state_context'
import { useGroupApi } from 'api/group_by'
import { useEffectSkipFirst, useForm, useQuery } from 'utils/hooks'
import { sampleStatusLogLocaleJa, SampleStatusLogSearchForm, SampleStatusLogType } from 'entities/sample_status_log'
import { EditMeasureLedgerForm } from './measure_ledger_form'

import { useFetchSampleStatusLogsApi } from 'api/sample_status_log'
import { useFetchMeasureLedgersApi } from 'api/measure_ledger'
import { LedgerFooter, LedgerHeader } from 'components/ledger/ledger_componet'

type Props = {
    history: H.History
}

export const MeasureLedgerPaperPage = (props: Props) => {
    const globalState = useContext(GlobalStateContext)
    const [showEdit, setShowEdit] = useState(false)
    const [measureLedger, setMeasureLedger] = useState<MeasureLedger>({})
    const query = useQuery<MeasureLedgerSearchForm>()
    const searchForm = useForm<MeasureLedgerSearchForm>({ ...query, ordering: 'measure_ledger_date' })
    const listApi = useFetchMeasureLedgersApi(searchForm)

    useEffect(() => {
        listApi.execute()
    }, [])

    useEffectSkipFirst(() => {
        globalState.setLoading(listApi.loading)
        if (listApi.isSuccess() && listApi.response.results.length == 1) {
            setMeasureLedger(listApi.response.results[0])
        }
    }, [listApi.loading])

    return (
        <>
            <ContentHeader
                className="no-print"
                headerTitle="測定作業日誌"
                right={
                    <>
                        <Button
                            onClick={() => {
                                window.print()
                            }}
                        >
                            印刷プレビュー
                        </Button>
                        {listApi.response.results.length == 1 && (
                            <Button variant="success" onClick={() => setShowEdit(true)}>
                                {measureLedger.staff !== null ? '編集' : '確認'}
                            </Button>
                        )}
                        <Button variant="secondary" onClick={() => props.history.push(`/ledger/measure?initialDate=${query.dateAfter}`)}>
                            一覧に戻る
                        </Button>
                    </>
                }
            />
            <EditMeasureLedgerForm
                show={showEdit}
                measureLedger={measureLedger}
                setMeasureLedger={setMeasureLedger}
                onClose={() => setShowEdit(false)}
                onComplete={() => {
                    setShowEdit(false)
                    listApi.execute()
                }}
            />
            {listApi.response.results.map((m) => (
                <MeasureLedgerPaper measureLedger={m} key={m.id} />
            ))}
        </>
    )
}

export default withRouter(MeasureLedgerPaperPage)

type MeasureLedgerPaperProps = {
    measureLedger: MeasureLedger
}

const MeasureLedgerPaper = (props: MeasureLedgerPaperProps) => {
    const globalState = useContext(GlobalStateContext)
    const sampleStatusLogGroupSearchForm = useForm<SampleStatusLogSearchForm>({
        date: props.measureLedger.date,
        groupBy: 'before_after',
        aggregate: 'Count("before_after")',
        aggregateAlias: 'count',
        isTest: false,
        isStandard: false,
    })
    const sampleStatusLogGroupApi = useGroupApi('sample_status_logs', sampleStatusLogGroupSearchForm)
    const sampleStatusLogSearchForm = useForm<SampleStatusLogSearchForm>({
        date: props.measureLedger.date,
        before: 'PCR_READY',
        isStandard: false,
    })
    const sampleStatusLogsApi = useFetchSampleStatusLogsApi(sampleStatusLogSearchForm)
    const inspectionStatusGroupSearchForm = useForm<SampleStatusLogSearchForm>({
        date: props.measureLedger.date,
        groupBy: 'inspection_status',
        aggregate: 'Count("inspection_status")',
        aggregateAlias: 'count',
    })
    const inspectionStatusGroupApi = useGroupApi('samples', inspectionStatusGroupSearchForm)

    useEffect(() => {
        inspectionStatusGroupApi.execute()
        sampleStatusLogsApi.execute()
        sampleStatusLogGroupApi.execute()
        setPageSize('portrait')
    }, [])

    useEffectSkipFirst(() => {
        sampleStatusLogGroupSearchForm.updateObject('date', props.measureLedger.date)
        sampleStatusLogSearchForm.updateObject('date', props.measureLedger.date)
        inspectionStatusGroupSearchForm.updateObject('date', props.measureLedger.date)
    }, [props.measureLedger.date])

    useEffect(() => {
        globalState.setLoading(sampleStatusLogsApi.loading)
    }, [sampleStatusLogsApi.loading])

    const getLogCount = (status: SampleStatusLogType): number => {
        const groups = sampleStatusLogGroupApi.response.groups
        const group = groups.find((g) => g.beforeAfter === status)
        return group?.count || 0
    }

    return (
        <A4 key={`measure_ledger_paper`} padding={0} landscape={false} style={{ paddingLeft: 50, paddingRight: 20 }}>
            <div className={'preview-result'} style={{ left: 30, padding: 20 }}>
                <LedgerHeader
                    headerTitle="RT-qPCR検査測定作業日誌"
                    recordTypeNum="RCL0025"
                    originalDocument={
                        <div>
                            <div>LSOP-RCL006</div>
                            <div>LSOP-RCL007</div>
                            <div>LSOP-RCL008</div>
                        </div>
                    }
                    versionNum="3"
                    timestamp={moment(props.measureLedger.updatedAt, 'YYYY/MM/DD HH:mm').format('YYYYMMDDHHmm')}
                ></LedgerHeader>

                <div style={{ fontSize: 14, textAlign: 'center' }}>
                    <table style={{ width: '100%' }}>
                        <tr>
                            <td colSpan={2} style={{ fontSize: 17, textAlign: 'start', fontWeight: 'bold', paddingTop: 15 }}>
                                測定作業内容
                            </td>
                        </tr>
                        <tbody>
                            <tr>
                                <td style={{ border: 'solid 1px' }}>作業工程</td>
                                <td style={{ border: 'solid 1px' }}>
                                    精製
                                    <input
                                        type={'checkbox'}
                                        style={{ marginLeft: 15 }}
                                        checked={getLogCount('WAREHOUSED__RNA_EXTRACTED') + getLogCount('PCR_READY__WAREHOUSED') === 0}
                                    />
                                    <label>N/A</label>
                                </td>
                                <td style={{ border: 'solid 1px' }}>
                                    cDNA合成
                                    <input
                                        type={'checkbox'}
                                        style={{ marginLeft: 15 }}
                                        checked={getLogCount('RNA_EXTRACTED__DNA_SYNTHESIZED') + getLogCount('PCR_READY__RNA_EXTRACTED') === 0}
                                    />
                                    <label>N/A</label>
                                </td>
                                <td style={{ border: 'solid 1px' }}>
                                    PCR
                                    <input
                                        type={'checkbox'}
                                        style={{ marginLeft: 15 }}
                                        checked={getLogCount('PCR_READY__COMPLETED') + getLogCount('PCR_READY__DNA_SYNTHESIZED') === 0}
                                    />
                                    <label>N/A</label>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ border: 'solid 1px' }}>作業日時</td>
                                <td style={{ border: 'solid 1px', textAlign: 'center' }}>
                                    {props.measureLedger.extractionStaffList && props.measureLedger.extractionStaffList.length > 0 ? (
                                        <div>
                                            <div>{`${props.measureLedger.date}`}</div>
                                            <div>{`${props.measureLedger.startTime}〜${props.measureLedger.endTime}`}</div>
                                        </div>
                                    ) : (
                                        <div>--</div>
                                    )}
                                </td>
                                <td style={{ border: 'solid 1px', textAlign: 'center' }}>
                                    {props.measureLedger.dnaStaffList && props.measureLedger.dnaStaffList.length > 0 ? (
                                        <div>
                                            <div>{`${props.measureLedger.date}`}</div>
                                            <div>{`${props.measureLedger.startTime}〜${props.measureLedger.endTime}`}</div>
                                        </div>
                                    ) : (
                                        <div>--</div>
                                    )}
                                </td>
                                <td style={{ border: 'solid 1px', textAlign: 'center' }}>
                                    {props.measureLedger.pcrRunStaffList && props.measureLedger.pcrRunStaffList.length > 0 ? (
                                        <div>
                                            <div>{`${props.measureLedger.date}`}</div>
                                            <div>{`${props.measureLedger.startTime}〜${props.measureLedger.endTime}`}</div>
                                        </div>
                                    ) : (
                                        <div>--</div>
                                    )}
                                </td>
                            </tr>
                            <tr>
                                <td style={{ border: 'solid 1px' }}>担当者名</td>
                                <td style={{ border: 'solid 1px' }}>
                                    {props.measureLedger.extractionStaffList && props.measureLedger.extractionStaffList.length > 0 ? (
                                        props.measureLedger.extractionStaffList.map((name, index) => {
                                            return <div key={'extraction_staff' + index}>{name}</div>
                                        })
                                    ) : (
                                        <div>--</div>
                                    )}
                                </td>
                                <td style={{ border: 'solid 1px' }}>
                                    {props.measureLedger.dnaStaffList && props.measureLedger.dnaStaffList.length > 0 ? (
                                        props.measureLedger.dnaStaffList.map((name, index) => {
                                            return <div key={'dna_staff' + index}>{name}</div>
                                        })
                                    ) : (
                                        <div>--</div>
                                    )}
                                </td>
                                <td style={{ border: 'solid 1px' }}>
                                    {props.measureLedger.pcrRunStaffList && props.measureLedger.pcrRunStaffList.length > 0 ? (
                                        props.measureLedger.pcrRunStaffList.map((name, index) => {
                                            return <div key={'pcr_staff' + index}>{name}</div>
                                        })
                                    ) : (
                                        <div>--</div>
                                    )}
                                </td>
                            </tr>
                            <tr>
                                <td style={{ border: 'solid 1px' }}>検体数</td>
                                <td style={{ border: 'solid 1px' }}>{getLogCount('WAREHOUSED__RNA_EXTRACTED')}</td>
                                <td style={{ border: 'solid 1px' }}>{getLogCount('RNA_EXTRACTED__DNA_SYNTHESIZED')}</td>
                                <td style={{ border: 'solid 1px' }}>{getLogCount('PCR_READY__COMPLETED')}</td>
                            </tr>
                            <tr>
                                <td style={{ border: 'solid 1px' }}>再検査検体数</td>
                                <td style={{ border: 'solid 1px' }}>{getLogCount('PCR_READY__WAREHOUSED')}</td>
                                <td style={{ border: 'solid 1px' }}>{getLogCount('PCR_READY__RNA_EXTRACTED')}</td>
                                <td style={{ border: 'solid 1px' }}>{getLogCount('PCR_READY__DNA_SYNTHESIZED')}</td>
                            </tr>
                        </tbody>

                        <tbody>
                            <tr>
                                <td colSpan={2} style={{ fontSize: 17, textAlign: 'start', fontWeight: 'bold', paddingTop: 15 }}>
                                    測定環境の状態
                                </td>
                            </tr>

                            <tr>
                                <td colSpan={2} style={{ border: 'solid 1px' }}>
                                    <div>設定温湿度の状態</div>
                                </td>
                                <td colSpan={2} style={{ border: 'solid 1px' }}>
                                    <div>
                                        <Flex justifyContent="center">
                                            <Flex alignItems="center">
                                                <input type={'checkbox'} checked={props.measureLedger.temperatureState} />
                                                <div>異常なし</div>
                                            </Flex>
                                            <Flex alignItems="center">
                                                <input type={'checkbox'} checked={!props.measureLedger.temperatureState} />
                                                <div>異常あり</div>
                                            </Flex>
                                        </Flex>
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2} style={{ border: 'solid 1px' }}>
                                    <div>設定機器の状態</div>
                                </td>
                                <td colSpan={2} style={{ border: 'solid 1px' }}>
                                    <div>
                                        <Flex justifyContent="center">
                                            <Flex alignItems="center">
                                                <input type={'checkbox'} checked={props.measureLedger.equipmentState} />
                                                <div>異常なし</div>
                                            </Flex>
                                            <Flex alignItems="center">
                                                <input type={'checkbox'} checked={!props.measureLedger.equipmentState} />
                                                <div>異常あり</div>
                                            </Flex>
                                        </Flex>
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2} style={{ border: 'solid 1px' }}>
                                    <div>検査試薬の状態</div>
                                </td>
                                <td colSpan={2} style={{ border: 'solid 1px' }}>
                                    <div>
                                        <Flex justifyContent="center">
                                            <Flex alignItems="center">
                                                <input type={'checkbox'} checked={props.measureLedger.probeState} />
                                                <div>異常なし</div>
                                            </Flex>
                                            <Flex alignItems="center">
                                                <input type={'checkbox'} checked={!props.measureLedger.probeState} />
                                                <div>異常あり</div>
                                            </Flex>
                                        </Flex>
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2} style={{ border: 'solid 1px' }}>
                                    <div>精度管理の状態</div>
                                </td>
                                <td colSpan={2} style={{ border: 'solid 1px' }}>
                                    <div>
                                        <Flex justifyContent="center">
                                            <Flex alignItems="center">
                                                <input type={'checkbox'} checked={props.measureLedger.qcReportState} />
                                                <div>異常なし</div>
                                            </Flex>
                                            <Flex alignItems="center">
                                                <input type={'checkbox'} checked={!props.measureLedger.qcReportState} />
                                                <div>異常あり</div>
                                            </Flex>
                                        </Flex>
                                    </div>
                                </td>
                            </tr>

                            <tr style={{ maxHeight: 400 }}>
                                <td colSpan={4} style={{ border: 'solid 1px', textAlign: 'left', verticalAlign: 'top', padding: '5px 10px' }}>
                                    <Flex flexDirection="column">
                                        <div>特記事項 : 事故及び対応記録等</div>
                                        <div style={{ width: '100%' }}>
                                            {props.measureLedger.note?.split('\n').map((line, i) => <div key={i}>{line}</div>) ?? <div>特記事項なし</div>}
                                        </div>
                                    </Flex>
                                </td>
                            </tr>
                        </tbody>
                        <tbody>
                            <tr>
                                <td style={{ fontSize: 17, textAlign: 'start', fontWeight: 'bold', paddingTop: 15 }}>異常データ</td>
                            </tr>

                            <tr>
                                <td colSpan={1} style={{ border: 'solid 1px', width: 100 }}>
                                    検体ID
                                </td>
                                <td colSpan={2} style={{ border: 'solid 1px' }}>
                                    対応
                                </td>
                                <td colSpan={1} style={{ border: 'solid 1px' }}>
                                    対応完了日
                                </td>
                            </tr>
                            {sampleStatusLogsApi.response.results.length > 0 ? (
                                sampleStatusLogsApi.response.results
                                    .filter((s) => ['DNA_SYNTHESIZED', 'RNA_EXTRACTED', 'WAREHOUSED'].includes(s.after!))
                                    .map((s) => (
                                        <tr style={{ height: 28 }} key={'hoge'}>
                                            <td colSpan={1} style={{ border: 'solid 1px', width: 150 }}>
                                                {s.sample?.code}
                                            </td>
                                            <td colSpan={2} style={{ border: 'solid 1px' }}>
                                                {sampleStatusLogLocaleJa[s.beforeAfter!]}
                                                {s.isForceChange ? '(Sample不足)' : ''}
                                            </td>
                                            <td colSpan={1} style={{ border: 'solid 1px' }}>
                                                {s.date}
                                            </td>
                                        </tr>
                                    ))
                            ) : (
                                <tr style={{ height: 28 }}>
                                    <td colSpan={1} style={{ border: 'solid 1px', width: 100 }}>
                                        --
                                    </td>
                                    <td colSpan={2} style={{ border: 'solid 1px' }}>
                                        --
                                    </td>
                                    <td colSpan={1} style={{ border: 'solid 1px' }}>
                                        --
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            <LedgerFooter page={1}></LedgerFooter>
        </A4>
    )
}
